import { useSyncExternalStore } from "react";

import { useAppAuth } from "./auth";
import { Drills } from "./screens/Drills";
import { Landing } from "./screens/Landing";
import { Lessons } from "./screens/Lessons";
import { SessionScreen } from "./screens/SessionScreen";
import { Settings } from "./screens/Settings";
import { Today } from "./screens/Today";
import { Varnamala } from "./screens/Varnamala";
import { applyDevSeed } from "./state/devSeed";

/**
 * Tiny history-backed router. No library: a `useSyncExternalStore`
 * subscription to `popstate` drives re-renders, and `navigate()` does a
 * `history.pushState` + a synthetic `popstate` so links stay in-app.
 *
 * /session runs a real practice session (Task 9). /varnamala, /lessons, and
 * /drills are real surfaces as of Task 17; any unknown route (and /settings
 * eventually) resolves to Today.
 */

function subscribe(callback: () => void): () => void {
  window.addEventListener("popstate", callback);
  return () => window.removeEventListener("popstate", callback);
}

function getSnapshot(): string {
  return window.location.pathname;
}

/** Current pathname, re-rendered on history changes. */
export function useLocation(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** In-app navigation: push a new path and notify subscribers. */
export function navigate(path: string): void {
  if (path === window.location.pathname) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function normalizePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

const ROUTES = [
  "/",
  "/session",
  "/varnamala",
  "/lessons",
  "/drills",
  "/settings",
] as const;

export function Router() {
  const pathname = normalizePath(useLocation());
  const route = (ROUTES as readonly string[]).includes(pathname)
    ? pathname
    : "/";
  const { mode, isSignedIn } = useAppAuth();

  // Dev-only visual-verification helper: ?dev-seed=1 marks l01-l08 complete
  // so the Varnamala/Lessons/Today screens show mixed mastery states.
  // `import.meta.env.DEV` is a build-time constant (false in prod builds),
  // so this call (and applyDevSeed's own body) is dead code once built for
  // production — it can never run outside `vite dev`/`vite preview` of a
  // dev build.
  //
  // Called directly in the render body (not a `useEffect`): every screen
  // (Varnamala/Lessons/Today) reads `loadState()` synchronously on its own
  // render with no store subscription, so a post-render effect here would
  // only ever be visible after a *second* render (e.g. the next
  // navigation). Running it before the switch below guarantees the seeded
  // state is already in localStorage by the time the routed screen's own
  // `loadState()` call executes on this same first render.
  // `applyDevSeed` is idempotent (checks `alreadySeeded` before writing),
  // so re-invoking it on every render — including React StrictMode's
  // double-invoke — and reading `window.location.search` are both safe.
  if (import.meta.env.DEV) {
    applyDevSeed();
    // Dev-only landing preview: dev mode is always "signed in", so the
    // clerk-mode landing gate below can never render there. `?landing-
    // preview=1` short-circuits to it so the landing can be developed and
    // visually verified without a Clerk key — same build-time guard
    // pattern as dev-seed above (dead code in production builds).
    if (
      new URLSearchParams(window.location.search).get("landing-preview") === "1"
    ) {
      return <Landing />;
    }
  }

  // Clerk mode, signed out → the minimal landing gate for every route.
  // Dev mode is always "signed in", so this never triggers there.
  if (mode === "clerk" && !isSignedIn) {
    return <Landing />;
  }

  switch (route) {
    case "/session":
      return <SessionScreen />;
    case "/settings":
      return <Settings />;
    case "/varnamala":
      return <Varnamala />;
    case "/lessons":
      return <Lessons />;
    case "/drills":
      return <Drills />;
    case "/":
    default:
      return <Today />;
  }
}
